// The `{footprint:kind;k=v;…}` token, e.g. `{footprint:pill;tone=critical;label=TIER 1}`. It's plain
// text, not a markdown link, because a `footprint:` link doesn't survive the editor — and raw text
// round-trips through markdown fine. The regex is bounded (ReDoS-safe), and values can't contain `|`
// so a token can't break a table cell.
export const INLINE_ENVELOPE_RE =
  /\{footprint:([a-z][a-z0-9-]{0,30})((?:;[a-z][a-z0-9-]{0,30}=[^{}|;\n]{0,120}){0,12})\}/g;

export type EnvelopeMatch = { index: number; raw: string; kind: string; rawParams: string };

export const matchEnvelope = (text: string): EnvelopeMatch[] => {
  const matches: EnvelopeMatch[] = [];
  for (const match of text.matchAll(INLINE_ENVELOPE_RE)) {
    if (match.index === undefined) continue;
    matches.push({ index: match.index, raw: match[0], kind: match[1], rawParams: match[2] });
  }
  return matches;
};

export const decodeParams = (rawParams: string): Record<string, string> => {
  const params: Record<string, string> = {};
  for (const segment of rawParams.split(';')) {
    if (!segment) continue;
    const eq = segment.indexOf('=');
    if (eq === -1) continue;
    params[segment.slice(0, eq)] = segment.slice(eq + 1);
  }
  return params;
};

export const encodeParams = (params: Record<string, string>): string =>
  Object.entries(params)
    .map(([key, value]) => `;${key}=${value}`)
    .join('');

// What authors write: `[label](footprint:kind?query)`. We turn it into the brace token before the
// editor sees it (a link wouldn't survive). The link text becomes `label` unless the query already set it.
const LINK_ENVELOPE_RE = /\[([^\]\n]*)\]\(footprint:([a-z][a-z0-9-]{0,30})(?:\?([^)\n]*))?\)/g;

export const linkToBrace = (markdown: string): string =>
  markdown.replace(LINK_ENVELOPE_RE, (_full, label: string, kind: string, query = '') => {
    const params: Record<string, string> = {};
    for (const pair of (query as string).split('&')) {
      if (!pair) continue;
      const eq = pair.indexOf('=');
      if (eq === -1) continue;
      params[decodeURIComponent(pair.slice(0, eq))] = decodeURIComponent(pair.slice(eq + 1));
    }
    if (label && !('label' in params)) params.label = label;
    return `{footprint:${kind}${encodeParams(params)}}`;
  });
